<script setup lang="ts">
import { computed } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import { getPepMotiveLabel } from '@/constants/bdcPepMotive';
import { isRecord, toNullableBoolean, toNullableString } from '@/utils/coerce';
import { formatDate } from '@/utils/formatters';
import type { KycCategory } from '@/types/kyc';

interface PepPositionRow {
  motive: string | null;
  isActive: boolean | null;
  startDate: string | null;
  endDate: string | null;
  department: string | null;
  jobTitle: string | null;
}

const props = defineProps<{ category: KycCategory }>();

/** `data_json` is a flat array from the pipeline: one row per position held by the PEP. */
const pepPositions = computed<PepPositionRow[]>(() => {
  const raw: unknown = props.category.data_json;
  if (!Array.isArray(raw)) return [];

  return raw.filter(isRecord).map((item) => ({
    motive: getPepMotiveLabel(toNullableString(item.position)),
    isActive: toNullableBoolean(item.is_active),
    startDate: toNullableString(item.start_date),
    endDate: toNullableString(item.end_date),
    department: toNullableString(item.department),
    jobTitle: toNullableString(item.job_title),
  }));
});
</script>

<template>
  <div class="mt-3 w-full min-w-0 max-w-full [contain:inline-size]">
    <DataTable :value="pepPositions" scrollable scroll-height="24rem" class="w-full max-w-full text-xs">
      <template #empty>
        <div class="p-6 text-center text-xs text-surface-500">Nenhum cargo PEP encontrado nesta análise.</div>
      </template>

      <Column field="motive" header="Motivo" sortable style="min-width: 8rem">
        <template #body="{ data: row }: { data: PepPositionRow }">
          <span class="text-surface-700">{{ row.motive || '—' }}</span>
        </template>
      </Column>
      <Column field="isActive" header="Status" sortable style="min-width: 6rem">
        <template #body="{ data: row }: { data: PepPositionRow }">
          <Tag
            v-if="row.isActive !== null"
            :value="row.isActive ? 'Ativo' : 'Inativo'"
            :severity="row.isActive ? 'danger' : 'secondary'"
            class="!py-0 !px-1.5 !text-[10px]"
          />
          <span v-else class="text-surface-400">—</span>
        </template>
      </Column>
      <Column field="startDate" header="Início" sortable style="min-width: 6.5rem">
        <template #body="{ data: row }: { data: PepPositionRow }">{{ formatDate(row.startDate, undefined, '—') }}</template>
      </Column>
      <Column field="endDate" header="Fim" sortable style="min-width: 6.5rem">
        <template #body="{ data: row }: { data: PepPositionRow }">{{ formatDate(row.endDate, undefined, '—') }}</template>
      </Column>
      <Column field="department" header="Local de Trabalho" style="min-width: 10rem; max-width: 16rem">
        <template #body="{ data: row }: { data: PepPositionRow }">
          <span :title="row.department || ''" class="block text-surface-700">{{ row.department || '—' }}</span>
        </template>
      </Column>
      <Column field="jobTitle" header="Título do Cargo" style="min-width: 10rem; max-width: 16rem">
        <template #body="{ data: row }: { data: PepPositionRow }">
          <span :title="row.jobTitle || ''" class="block text-surface-700">{{ row.jobTitle || '—' }}</span>
        </template>
      </Column>
    </DataTable>
  </div>
</template>
